import { apiFetch } from "./api";

/**
 * Rostering (FEAT-2026-017 phase 3).
 *
 * An entry is the most specific calendar statement for a date. `shiftId: null`
 * means **explicitly off** — different from having no entry at all, where the
 * employee falls back to their default shift.
 */
export interface RosterEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  /** `YYYY-MM-DD`. */
  date: string;
  shiftId: string | null;
  shiftName: string | null;
  notes: string | null;
}

export const rosterApi = {
  list: (params: { dateFrom: string; dateTo: string; siteId?: string; siteIds?: string[]; employeeId?: string }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      // Lists go as `a,b` (FEAT-2026-030); an empty one is no filter.
      if (Array.isArray(v)) { if (v.length) q.set(k, v.join(",")); }
      else if (v) q.set(k, v);
    }
    return apiFetch<RosterEntry[]>(`/roster?${q.toString()}`);
  },

  /** `shiftId: null` rosters the person OFF for that date. */
  setEntry: (body: { employeeId: string; date: string; shiftId: string | null; notes?: string }) =>
    apiFetch<{ id: string }>("/roster/entry", { method: "POST", body }),

  /** Safe to re-run — it upserts. */
  apply: (body: {
    employeeIds: string[];
    dateFrom: string;
    dateTo: string;
    shiftId?: string | null;
    onlyShiftWorkingDays?: boolean;
    overwriteExisting?: boolean;
  }) => apiFetch<{ written: number; skipped: number }>("/roster/apply", { method: "POST", body }),

  /** Removes entries so the default shift applies again — not the same as OFF. */
  clear: (body: { employeeIds: string[]; dateFrom: string; dateTo: string }) =>
    apiFetch<{ removed: number }>("/roster/clear", { method: "POST", body }),
};

/** Monday-anchored week containing `iso`, as seven `YYYY-MM-DD` strings. */
export function weekOf(iso: string): string[] {
  const [y, m, d] = iso.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  // getUTCDay: 0 = Sunday. Shift back to the Monday of this week.
  const offset = (base.getUTCDay() + 6) % 7;
  base.setUTCDate(base.getUTCDate() - offset);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(base);
    day.setUTCDate(base.getUTCDate() + i);
    return day.toISOString().slice(0, 10);
  });
}

export function shiftWeek(iso: string, weeks: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + weeks * 7);
  return dt.toISOString().slice(0, 10);
}
